"use client";

import { Globe } from "lucide-react";
import { useLocaleStore, SUPPORTED_LOCALES, type Locale } from "@/store/locale.store";

const LABELS: Record<Locale, string> = { fr: "FR", en: "EN" };

/**
 * Inline language switcher. Used wherever a quick FR/EN swap should be reachable
 * without opening a menu (sidebar, landing header, mobile nav…).
 *
 * - default: a segmented "FR | EN" pill.
 * - compact: a single globe button that toggles to the other language (for the
 *   collapsed, icon-only sidebar).
 */
export function LanguageToggle({
  compact = false,
  className = "",
}: {
  compact?: boolean;
  className?: string;
}) {
  const { locale, setLocale } = useLocaleStore();

  if (compact) {
    const next: Locale = locale === "fr" ? "en" : "fr";
    return (
      <button
        type="button"
        onClick={() => setLocale(next)}
        title={`${LABELS[locale]} → ${LABELS[next]}`}
        aria-label="Change language"
        className={`flex flex-col items-center justify-center gap-0.5 rounded-lg px-3 py-2 text-gray-500 transition hover:bg-gray-50 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-[#313131] dark:hover:text-gray-200 ${className}`}
      >
        <Globe className="h-5 w-5" />
        <span className="text-[10px] font-bold leading-none">{LABELS[locale]}</span>
      </button>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-0.5 rounded-full border border-gray-200 p-0.5 dark:border-[#313131] ${className}`}
    >
      {SUPPORTED_LOCALES.map((code) => {
        const active = locale === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => setLocale(code)}
            aria-pressed={active}
            className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${
              active
                ? "bg-secondary text-white dark:bg-primary dark:text-[#1E1E1E]"
                : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            }`}
          >
            {LABELS[code]}
          </button>
        );
      })}
    </div>
  );
}
